import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { logActivity, ACTIONS } from '../lib/activity'
import { getActiveCrew, normalizeCrewName } from '../lib/crew'
import { toISODateString } from '../lib/dateUtils'
import JobModal from '../components/JobModal'
import PropertyModal from '../components/PropertyModal'
import PropertyJobsModal from '../components/PropertyJobsModal'
import { escapeIlike } from '../lib/searchUtils'
import { googleMapsUrl } from '../lib/mapUtils'

const PAGE_SIZE = 50

export default function Properties() {
  const [properties, setProperties] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [jobModalOpen, setJobModalOpen] = useState(false)
  const [jobInitialData, setJobInitialData] = useState(null)
  const [crewMembers, setCrewMembers] = useState([])
  const [historyProperty, setHistoryProperty] = useState(null)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(0) // Reset to first page on new search
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const load = useCallback(async (isMounted) => {
    if (!supabase) return
    setLoading(true)
    try {
      // Build query
      let query = supabase
        .from('properties')
        .select('*', { count: 'exact' })
        .neq('is_deleted', true)

      // Add search filter (server-side)
      if (debouncedSearch) {
        const searchTerm = `%${escapeIlike(debouncedSearch)}%`
        query = query.or(`name.ilike.${searchTerm},address.ilike.${searchTerm},management_company.ilike.${searchTerm}`)
      }

      // Add pagination and ordering
      const from = page * PAGE_SIZE
      const to = from + PAGE_SIZE - 1
      query = query.order('name').range(from, to)

      const { data, error, count } = await query
      if (!error && isMounted.current) {
        setProperties(data || [])
        setTotalCount(count || 0)
      }
    } catch (e) {
      console.warn('Could not load properties:', e.message)
    } finally {
      if (isMounted.current) setLoading(false)
    }
  }, [debouncedSearch, page])

  useEffect(() => {
    const isMounted = { current: true }
    load(isMounted)
    return () => { isMounted.current = false }
  }, [load])

  // Load crew members on mount
  useEffect(() => {
    const loadCrew = async () => {
      const crew = await getActiveCrew()
      setCrewMembers(crew)
    }
    loadCrew()
  }, [])

  const handleSave = async (form) => {
    // Coerce unit_count to integer or null — Postgres rejects empty string for integer columns
    const sanitized = {
      ...form,
      unit_count: form.unit_count === '' || form.unit_count === null || form.unit_count === undefined
        ? null
        : Number(form.unit_count),
    }
    if (!supabase) {
      if (sanitized.id) {
        setProperties(prev => prev.map(p => p.id === sanitized.id ? { ...p, ...sanitized } : p))
      } else {
        setProperties(prev => [...prev, { id: `local-${Date.now()}`, ...sanitized }])
      }
      return
    }
    if (sanitized.id) {
      const { error } = await supabase
        .from('properties')
        .update({ ...sanitized, updated_at: new Date().toISOString() })
        .eq('id', sanitized.id)
      if (error) throw error
      await logActivity(ACTIONS.PROPERTY_UPDATED, 'property', sanitized.id, `Updated property: ${sanitized.name}`)
    } else {
      const { data, error } = await supabase.from('properties').insert([sanitized]).select().single()
      if (error) throw error
      await logActivity(ACTIONS.PROPERTY_CREATED, 'property', data?.id, `Created property: ${sanitized.name}`)
    }
    await load()
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this property?')) return
    const prop = properties.find(p => p.id === id)
    if (!supabase) {
      setProperties(prev => prev.filter(p => p.id !== id))
      return
    }
    const { error } = await supabase
      .from('properties')
      .update({ is_deleted: true, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (!error) {
      await logActivity(ACTIONS.PROPERTY_DELETED, 'property', id, `Deleted property: ${prop?.name || 'Unknown'}`)
      setProperties(prev => prev.filter(p => p.id !== id))
    }
  }

  const handleScheduleJob = (property) => {
    setJobInitialData({
      property_id: property.id,
      property_name: property.name,
      property: property,
      job_date: toISODateString(new Date()),
      start_time: '08:00',
      end_time: '09:00',
      crew_name: '',
      comments: '',
      status: 'scheduled',
      flag: 'normal',
    })
    setJobModalOpen(true)
  }

  const handleSaveJob = async (form) => {
    if (!supabase) return

    const propName = form.property_name || 'Unknown'

    // Strip synthetic/joined fields that are not DB columns
    // eslint-disable-next-line no-unused-vars
    const { property, property_name, id: _id, ...dbFields } = form
    dbFields.crew_name = normalizeCrewName(dbFields.crew_name)

    if (form.id) {
      // Update existing job
      const { error } = await supabase
        .from('jobs')
        .update({ ...dbFields, updated_at: new Date().toISOString() })
        .eq('id', form.id)
      if (error) throw error
      await logActivity(ACTIONS.JOB_UPDATED, 'job', form.id, `Updated job at ${propName}`)
    } else {
      // Create new job
      const { data, error } = await supabase
        .from('jobs')
        .insert([{ ...dbFields }])
        .select()
        .single()
      if (error) {
        if (error.code === '23505') {
          throw new Error('This time slot is already booked for this crew member')
        }
        throw error
      }
      await logActivity(ACTIONS.JOB_CREATED, 'job', data?.id, `Created job at ${propName}`)
    }
  }

  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  return (
    <div className="h-full overflow-y-auto">
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Properties</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {debouncedSearch
              ? `${totalCount} result${totalCount !== 1 ? 's' : ''} for "${debouncedSearch}"`
              : `${totalCount} properties`
            }
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setModalOpen(true) }}
          className="flex items-center gap-1.5 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Property
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search properties..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <svg className="animate-spin w-6 h-6 text-brand-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      ) : properties.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <p className="text-sm">{debouncedSearch ? 'No properties match your search' : 'No properties yet. Add your first one!'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {properties.map(prop => (
            <div key={prop.id} className={`rounded-xl border p-4 transition-colors ${
              prop.do_not_service
                ? 'bg-red-50 border-red-300 hover:bg-red-100 hover:border-red-400'
                : prop.past_due_invoice
                ? 'bg-orange-50 border-orange-300 hover:bg-orange-100 hover:border-orange-400'
                : 'bg-white border-gray-200 hover:border-brand-200'
            }`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className={`font-semibold ${prop.do_not_service ? 'text-red-700' : prop.past_due_invoice ? 'text-orange-700' : 'text-gray-900'}`}>{prop.name}</h3>
                    {prop.do_not_service && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-300">
                        ⛔ Do Not Service
                      </span>
                    )}
                    {prop.past_due_invoice && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-300">
                        ⚠️ Past Due Invoice
                      </span>
                    )}
                  </div>
                  <p className={`text-sm mt-0.5 ${prop.do_not_service ? 'text-red-600' : prop.past_due_invoice ? 'text-orange-600' : 'text-gray-500'}`}>{prop.address}</p>

                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                    {prop.phone && (
                      <span className="text-xs text-gray-500">📞 {prop.phone}</span>
                    )}
                    {prop.management_company && (
                      <span className="text-xs text-gray-500">🏢 {prop.management_company}</span>
                    )}
                    {prop.unit_count && (
                      <span className="text-xs text-gray-500">🏠 {prop.unit_count} units</span>
                    )}
                  </div>

                  {(prop.property_manager_name || prop.maintenance_supervisor_name) && (
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                      {prop.property_manager_name && (
                        <span className="text-xs text-gray-500">
                          Mgr: {prop.property_manager_name}
                          {prop.property_manager_phone && ` · ${prop.property_manager_phone}`}
                        </span>
                      )}
                      {prop.maintenance_supervisor_name && (
                        <span className="text-xs text-gray-500">
                          Maint: {prop.maintenance_supervisor_name}
                          {prop.maintenance_supervisor_phone && ` · ${prop.maintenance_supervisor_phone}`}
                        </span>
                      )}
                    </div>
                  )}

                  {prop.standing_directions && (
                    <p className="mt-2 text-xs text-gray-400 italic truncate">
                      📋 {prop.standing_directions}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {prop.address && (
                    <a
                      href={googleMapsUrl(prop.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                      title="Open in Google Maps"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </a>
                  )}
                  <button
                    onClick={() => handleScheduleJob(prop)}
                    className="px-3 py-1.5 text-xs font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 rounded-lg transition-colors flex items-center gap-1"
                    title="Schedule Job"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Schedule
                  </button>
                  <button
                    onClick={() => setHistoryProperty(prop)}
                    className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                    title="Job History"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => { setEditing(prop); setModalOpen(true) }}
                    className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                    title="Edit Property"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(prop.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete Property"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <PropertyModal
        key={editing?.id || 'new'}
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null) }}
        onSave={handleSave}
        initialData={editing}
      />

      <JobModal
        isOpen={jobModalOpen}
        onClose={() => setJobModalOpen(false)}
        onSave={handleSaveJob}
        onDelete={() => {}}
        initialData={jobInitialData}
        crewMembers={crewMembers}
      />

      <PropertyJobsModal
        isOpen={!!historyProperty}
        onClose={() => setHistoryProperty(null)}
        property={historyProperty}
      />
    </div>
    </div>
  )
}
